import { createRef, useCallback, useLayoutEffect, useRef, useState } from "react";
import { Waypoint } from "react-waypoint";
import classNames from "classnames";
import { brandPropTypes } from "app/utils/propTypes";
import { pushTagOnGridViewActive, pushTagOnListViewActive } from "app/utils/analytics";
import HeadMetadata from "app/utils/metas/HeadMetadata";
import PropTypes from "prop-types";
import "./Listing.scss";
import { IS_DEFAULT_LISTING_VIEW_LIST } from "src/@brand/appConstants";
import ListingHeaderContainer from "app/pages/Listing/ListingHeader/ListingHeaderContainer";
import { BRANDS } from "app/constants";
import { AsideProductFiltersContainer } from "app/pages/.shared/AsidePanel/AsideProductFilters/AsideProductFiltersContainer";
import { AsideProductFiltersMenu } from "@brand/AsidePanel/AsideProductFilters/AsideProductFiltersMenu";
import ProductsFilterContainer from "@brand/ProductsFilter/ProductsFilterContainer";
import { StickyContainer } from "react-sticky";
import { scrollToElement } from "app/utils/scroller";
import { forceCheck } from "react-lazyload";
import CookiePolicyFooterContainer from "app/pages/.shared/CookiePolicyFooter/CookiePolicyFooterContainer";
import ListingContentContainer from "app/pages/Listing/ListingContent/ListingContentContainer";
import IconGoToTopArrow from "app/pages/.shared/static/icons/IconGoToTopArrow";
import { PushNotificationContainer } from "app/pages/.shared/PushNotification/PushNotificationContainer";
import PartnerSEO from "app/pages/.shared/PartnerSEO/PartnerSEO";
import ProductsFilter from "@brand/ProductsFilter/ProductsFilter";
import useOnScreen from "app/pages/.shared/useOnScreen";

const Listing = ({
	brand,
	idSpotlight,
	prefetchedPhotos,
	displayProductsCount,
	listingSEO = {},
	resolution,
}) => {
	const [listViewDisplay, setListViewDisplay] = useState(IS_DEFAULT_LISTING_VIEW_LIST);
	const [showScrollTopButton, setShowScrollTopButton] = useState(false);

	const listingHeaderRef = createRef();
	const filtersRef = useRef();

	const isElementOnScreen = useOnScreen(filtersRef);

	const filterClassname = classNames("listing__filters");

	const asideProductFiltersMenuComponent = useCallback(
		props => {
			return (
				(brand === BRANDS.AF || brand === BRANDS.TO || brand === BRANDS.UG) && (
					<AsideProductFiltersMenu
						scrollToListingHeader={scrollToListingHeader}
						{...props}
					/>
				)
			);
		},
		[brand]
	);

	useLayoutEffect(() => {
		// force l'affichage des images des produits sur mobile lorsqu'un filtre est actif
		forceCheck();
	}, []);

	const scrollToListingHeader = () => {
		scrollToElement({
			element: listingHeaderRef.current,
			offset: 0,
			disableSmoothScroll: true,
		});
	};

	const toogleCardView = useCallback(() => {
		setListViewDisplay(false);
		pushTagOnGridViewActive();
	}, []);

	const toogleListView = useCallback(() => {
		setListViewDisplay(true);
		pushTagOnListViewActive();
	}, []);

	const displayScrollTopButton = useCallback(({ previousPosition, currentPosition }) => {
		if (previousPosition === Waypoint.above && currentPosition === Waypoint.inside) {
			setShowScrollTopButton(false);
		}
	}, []);

	const hideScrollTopButton = useCallback(({ previousPosition, currentPosition }) => {
		if (previousPosition === Waypoint.inside && currentPosition === Waypoint.above) {
			setShowScrollTopButton(true);
		}
	}, []);

	const handleScrollToTopClick = useCallback(() => {
		window.scrollTo(0, 0);
	}, []);

	const scrollTopButtonClass = classNames({
		"scroll-top-button": true,
		"scroll-top-button--visible": showScrollTopButton,
	});

	const waypointScrollableAncestor = typeof window !== "undefined" ? window : undefined;

	return (
		<StickyContainer>
			<AsideProductFiltersContainer>
				<ProductsFilterContainer
					asideFilterMenu={false}
					renderComponent={asideProductFiltersMenuComponent}
				/>
			</AsideProductFiltersContainer>

			<div className="listing">
				<HeadMetadata prefetchRessources={prefetchedPhotos} page="listing" />
				<ListingHeaderContainer
					idSpotlight={idSpotlight}
					isListViewDisplay={listViewDisplay}
					toogleCardView={toogleCardView}
					toogleListView={toogleListView}
					scrollToListingHeader={scrollToListingHeader}
					listingHeaderRef={listingHeaderRef}
					displayProductsCount={displayProductsCount}
				/>

				<Waypoint
					topOffset="-2500px"
					scrollableAncestor={waypointScrollableAncestor}
					onEnter={displayScrollTopButton}
					onLeave={hideScrollTopButton}
				/>

				<ProductsFilterContainer
					scrollToListingHeader={scrollToListingHeader}
					renderComponent={props => (
						<ProductsFilter
							{...props}
							className={filterClassname}
							filtersRef={filtersRef}
							isStuck={isElementOnScreen}
						/>
					)}
				/>

				<ListingContentContainer
					displayProductsCount={displayProductsCount}
					spotlightUri={idSpotlight}
					isListViewDisplay={listViewDisplay}
				/>

				<PartnerSEO
					title={listingSEO.title}
					description={listingSEO.description}
					resolution={resolution}
				/>

				<span className={scrollTopButtonClass} onClick={handleScrollToTopClick}>
					<IconGoToTopArrow width={50} height={50} />
				</span>

				<CookiePolicyFooterContainer />
			</div>
			<PushNotificationContainer />
		</StickyContainer>
	);
};

Listing.propTypes = {
	brand: brandPropTypes,
	prefetchedPhotos: PropTypes.arrayOf(PropTypes.string),
	idSpotlight: PropTypes.string,
	listingSEO: PropTypes.object,
	displayProductsCount: PropTypes.bool,
	resolution: PropTypes.string,
};

export default Listing;
